import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from '@/design-system/ui/Icon';
import { Reveal } from '@/design-system/ui/Reveal';
import { cn } from '@/design-system/ui/cn';
import { toPersianDigits } from '@/core/format/numbers';
import { useReducedMotion } from '@/core/theme/use-reduced-motion';
import { HeroWaves } from './HeroWaves';

const SERVICES: { icon: IconName; title: string; text: string }[] = [
  { icon: 'car', title: 'سفر عادی', text: 'رزرو صندلی در تاکسی‌های بین‌شهری با بهترین قیمت' },
  { icon: 'route', title: 'سفر دربستی', text: 'کل خودرو در اختیار شماست؛ بدون مسافر متفرقه' },
  { icon: 'box', title: 'ارسال مرسوله', text: 'ارسال سریع و مطمئن بسته‌ها به شهرهای مختلف' },
];

/** `count` stats animate up to their value; text stats are shown as they are. */
const STATS: { icon: IconName; label: string; count?: number; title?: string }[] = [
  { icon: 'verified', label: 'راننده‌ی مطمئن', count: 200 },
  { icon: 'users', label: 'مسافر راضی', count: 5000 },
  { icon: 'phone', title: 'پشتیبانی', label: '۲۴ ساعت' },
  { icon: 'wallet', title: 'قیمت شفاف', label: 'بدون هزینه پنهان' },
];

const fmt = (n: number) => toPersianDigits(Math.round(n).toLocaleString('en-US')).replace(/,/g, '٬');

/** Counts from 0 to `to` the first time it scrolls into view. Server render and reduced-motion show the final number. */
function CountUp({ to }: { to: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const still = useReducedMotion();
  const [value, setValue] = useState(to);

  useEffect(() => {
    const el = ref.current;
    if (still || !el || typeof IntersectionObserver !== 'function' || typeof requestAnimationFrame !== 'function') return;
    let raf = 0;
    setValue(0);
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        io.disconnect();
        const t0 = performance.now();
        const tick = (now: number) => {
          const p = Math.min(1, (now - t0) / 1800);
          setValue(to * (1 - Math.pow(1 - p, 3))); // ease-out
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [to, still]);

  return (
    <span ref={ref} dir="ltr" className="tabular-nums">
      +{fmt(value)}
    </span>
  );
}

const glass =
  'rounded-[28px] border border-white/25 bg-white/[0.06] shadow-[inset_0_1px_0_rgb(255_255_255/0.08)] backdrop-blur-md transition duration-300 hover:-translate-y-1 hover:border-mint/50 hover:bg-white/[0.11]';
const iconCircle = 'grid size-14 shrink-0 place-items-center rounded-full bg-mint/20 text-mint ring-1 ring-mint/25';

/** Dark glass "Blino at a glance" band: what we offer, and the numbers that build trust. */
export function BlinoAtAGlance() {
  return (
    <section
      aria-labelledby="glance-heading"
      className="relative isolate mt-14 overflow-hidden px-4 py-14 text-white sm:px-8 md:py-20 lg:px-12 [background:linear-gradient(180deg,#3b5d78_0%,#2a4a5f_55%,#1d3a4c_100%)]"
    >
      <HeroWaves fixed className="pointer-events-none absolute -z-10 max-w-none" />

      <div>
      <Reveal>
        <div className="text-center">
          <h2 id="glance-heading" className="inline-flex items-center gap-3 text-2xl font-extrabold md:text-3xl">
            <span className="h-0.5 w-8 rounded-full bg-mint" aria-hidden />
            بلینو در یک نگاه
            <span className="h-0.5 w-8 rounded-full bg-mint" aria-hidden />
          </h2>
          <p className="mt-2 text-sm text-white/70">آمار و شاخص‌های اعتماد به بلینو</p>
        </div>
      </Reveal>

      <ul className="mt-9 grid gap-4 md:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal as="li" key={s.title} delay={i * 90} className={cn(glass, 'flex items-center gap-4 p-5')}>
            <span className={iconCircle}><Icon name={s.icon} size={26} /></span>
            <span className="min-w-0">
              <span className="block text-lg font-bold">{s.title}</span>
              <span className="mt-0.5 block text-sm leading-7 text-white/75">{s.text}</span>
            </span>
          </Reveal>
        ))}
      </ul>

      <ul className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal as="li" key={s.label} delay={280 + i * 90} className={cn(glass, 'flex flex-col items-start gap-3 p-4 sm:flex-row sm:items-center sm:gap-4 sm:p-5')}>
            <span className={iconCircle}><Icon name={s.icon} size={26} /></span>
            <span className="min-w-0">
              <span className="block text-xl font-extrabold sm:text-2xl">{s.count ? <CountUp to={s.count} /> : s.title}</span>
              <span className="mt-0.5 block text-sm text-white/75">{s.label}</span>
            </span>
          </Reveal>
        ))}
      </ul>
      </div>
    </section>
  );
}
